import { Settings } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, type SvgIconProps, TablePagination, TextField } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import InnerLayout from '@src/App/Components/Core/Layouts/InnerLayout/InnerLayout';
import type { ITabConfig } from '@src/App/Components/Core/Layouts/LayoutElements/MvpTabContainer';
import { useTableSettingsDialog } from '@src/App/Components/Dialogs/TableSettingsDialog/TableSettingsDialog';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import type { IDataTableField, IDataTableSettings } from '@src/App/Components/Tables/DataTable/DataTable';
import { hashToJson, jsonToHash } from '@src/helper/helper';
import type { Entity } from '@src/Objects/Entity';
import type { AppState } from '@store/store';
import React, { type ReactNode, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ViewMenu } from './ViewMenu';
import { type IViewType, ViewTypeMenu } from './ViewTypeMenu';

export enum EViewType {
    TABLE = 'table',
    CARD = 'card',
    TREE = 'tree',
    ORGUNIT = 'orgunit',
}

export interface IView<T extends Entity> {
    id: string;
    title: string;
    filter: (entity: T) => boolean;
}

export interface IEntitiesPageAction {
    id: string;
    text: string;
    icon: React.ReactElement<SvgIconProps>;
    filter: (selectedCount: number) => boolean;
    action: (selectedIDs: number[]) => void;
}

interface EntitiesPageProps<T extends Entity> {
    title: string;
    subTitle?: string;
    caption?: string;
    icon?: React.ReactNode;
    entities: T[] | null;
    types: IViewType<T>[];
    views: IView<T>[];
    actions: IEntitiesPageAction[];
    selected: number[];
    loadingError: CustomGraphQLError | null;
    tabs?: ITabConfig[];
    buttons?: ReactNode;
    chips?: ReactNode;
    filter: (entity: T, selectedView: IView<T>, searchText: string) => boolean;
    setSelected: (selected: number[]) => void;
    children?: JSX.Element;
    tableFields?: IDataTableField<any>[];
    tableId?: string;
}

const CONST_TABLE_MIN_WITH = 350;
/**
 * Represents a page displaying a list of entities.
 *
 * @param {EntitiesPageProps<T>} props - The props for the EntitiesPage component.
 * @returns {JSX.Element} - The rendered EntitiesPage component.
 */
function EntitiesPage<T extends Entity>(props: EntitiesPageProps<T>) {
    const width = useSelector((state: AppState) => state.windowSize.width);
    const types = props.types.filter(
        (t) =>
            t.id !== EViewType.TABLE ||
            (!t.minWidth && width >= CONST_TABLE_MIN_WITH) ||
            (t.minWidth && t.minWidth < width),
    );
    const loaded = props.entities !== null;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).entityPage;
    const offline = useSelector((state: AppState) => state.client.offline);
    const [searchText, setSearchText] = React.useState<string>('');
    const [type, setType] = React.useState<IViewType<T>>(types[0]);
    const [selectedView, setSelectedView] = React.useState<IView<T>>(props.views[0]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);
    const [tableSettings, setTableSettings] = React.useState<IDataTableSettings | null>(null);
    const openTableSettingsDialog = useTableSettingsDialog();

    // Load table settings from localStorage
    useEffect(() => {
        if (props.tableId && props.tableFields) {
            const storedSettings = localStorage.getItem(`tableSettings-${props.tableId}`);
            if (storedSettings) {
                setTableSettings(JSON.parse(storedSettings));
            } else {
                const initialSettings = {
                    hiddenFields: props.tableFields.reduce((acc, field) => ({ ...acc, [field.id]: false }), {}),
                    order: props.tableFields.map((field) => field.id),
                };
                setTableSettings(initialSettings);
                localStorage.setItem(`tableSettings-${props.tableId}`, JSON.stringify(initialSettings));
            }
        }
    }, [props.tableId, props.tableFields]);

    useEffect(() => {
        const parameter = hashToJson<{ viewId: string; typeId: string; search: string }>(window.location.hash);
        const { viewId, typeId, search } = parameter;

        const searchedType = types.find((t) => t.id === typeId);
        const initialType = typeId && searchedType ? searchedType : types[0];
        const searchedView = props.views.find((v) => v.id === viewId);
        const initialView = viewId && searchedView ? searchedView : props.views[0];
        setType(initialType);
        setSelectedView(initialView);
        setSearchText(search ? decodeURI(search) : '');
    }, [types, props.views]); // Removed viewId and typeId from dependencies, as they are not used directly

    const changeSearch = (search: string) => {
        const parameter = hashToJson<{ viewId: string; typeId: string; search?: string }>(window.location.hash);
        setSearchText(search || '');
        parameter.search = search;
        if (search === '') {
            delete parameter.search;
        }
        window.location.hash = jsonToHash(parameter);
    };
    const changeSelectedView = (view: IView<T>) => {
        const parameter = hashToJson<{ viewId: string; typeId: string; search: string }>(window.location.hash);
        setSelectedView(view);
        props.setSelected([]);
        parameter.viewId = view.id;
        window.location.hash = jsonToHash(parameter);
    };
    const changeType = (type: IViewType<T>) => {
        const parameter = hashToJson<{ viewId: string; typeId: string; search: string }>(window.location.hash);
        setType(type);
        props.setSelected([]);
        parameter.typeId = type.id;
        window.location.hash = jsonToHash(parameter);
    };
    useEffect(() => {
        const available = types.some((t) => t.id === type.id);
        if (!available) {
            changeType(types[0]);
        }
    }, [width, types]);
    const error = props.loadingError;
    const errorMessage = error
        ? error.statusCode === 401
            ? langStrings.notAllowed
            : error.statusCode === 408
              ? langStrings.offline
              : error.message
        : '';

    if (error)
        return (
            <InnerLayout title={errorMessage}>
                <Box sx={{ p: 2, textAlign: 'center' }}>{errorMessage}</Box>
            </InnerLayout>
        );
    const visibleEntities = props.entities
        ? props.entities.filter((e) => props.filter(e, selectedView, searchText))
        : [];
    return (
        <InnerLayout
            title={props.title}
            subTitle={props.subTitle}
            caption={props.caption}
            icon={props.icon}
            buttons={props.buttons}
        >
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <ViewMenu views={props.views} selectedView={selectedView} setSelectedView={changeSelectedView} />
                <ViewTypeMenu types={types} selected={type} onChange={changeType} />
            </Box>
            {loaded && props.entities ? (
                <Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                                {props.actions.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {props.actions.map((action) => (
                                            <Button
                                                key={action.id}
                                                variant={action.id === 'create' ? 'contained' : 'outlined'}
                                                size={width <= 600 ? 'small' : 'medium'}
                                                startIcon={action.icon}
                                                disabled={!action.filter(props.selected.length) || offline}
                                                onClick={() => action.action(props.selected)}
                                                sx={{
                                                    borderRadius: '24px',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    px: 3,
                                                    ...(action.id === 'create' && {
                                                        background: (theme) =>
                                                            `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                                                        boxShadow: (theme) =>
                                                            `0 3px 5px 2px ${theme.palette.primary.main}30`,
                                                    }),
                                                }}
                                            >
                                                {action.text}
                                            </Button>
                                        ))}
                                    </Box>
                                )}
                                {props.chips}
                            </Box>
                            <TextField
                                size="small"
                                variant="outlined"
                                label={langStrings.search}
                                value={searchText}
                                onChange={(e) => changeSearch(e.target.value)}
                                sx={{
                                    maxWidth: 400,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'background.paper',
                                        '& fieldset': {
                                            borderColor: 'divider',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'text.secondary',
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Suspense fallback={<LoadingBox />}>
                                {type.renderContainer(
                                    props.entities,
                                    type.id === EViewType.TABLE
                                        ? visibleEntities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        : visibleEntities,
                                    width,
                                    props.selected,
                                    tableSettings,
                                )}
                            </Suspense>
                        </Box>
                        {type.id === EViewType.TABLE && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                <TablePagination
                                    rowsPerPageOptions={[25, 50, 100]}
                                    component="div"
                                    count={visibleEntities.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={(event, newPage) => setPage(newPage)}
                                    onRowsPerPageChange={(event) => {
                                        setRowsPerPage(parseInt(event.target.value, 10));
                                        setPage(0);
                                    }}
                                    sx={{ flexGrow: 1 }}
                                />
                                <IconButton
                                    size="small"
                                    aria-label="settings"
                                    onClick={async () => {
                                        if (props.tableFields && props.tableId) {
                                            const currentSettings = tableSettings || {
                                                hiddenFields: props.tableFields.reduce(
                                                    (acc, field) => ({ ...acc, [field.id]: false }),
                                                    {},
                                                ),
                                                order: props.tableFields.map((field) => field.id),
                                            };
                                            const newSettings = await openTableSettingsDialog(
                                                props.tableId,
                                                props.tableFields,
                                                currentSettings,
                                            );
                                            setTableSettings(newSettings);
                                            localStorage.setItem(
                                                `tableSettings-${props.tableId}`,
                                                JSON.stringify(newSettings),
                                            );
                                        }
                                    }}
                                    sx={{ ml: 2 }}
                                >
                                    <Settings />
                                </IconButton>
                            </Box>
                        )}
                        {props.children && <Box sx={{ mt: 2 }}>{props.children}</Box>}
                    </Box>
                </Box>
            ) : (
                <LoadingBox />
            )}
        </InnerLayout>
    );
}

export default EntitiesPage;
